import React from 'react';

/* --bg-overlay, --shadow-overlay, --radius-panel, 1px --border-default (mandatory
   in light — R4). Backdrop --scrim. Esc closes unless a typed token is pending.
   Cancel left, confirm right, confirm NEVER pre-focused. The kill-switch modal is
   the only container in the product with a 1px --loss border, which makes it
   identifiable before it is read.

   ## Motion

   Machined register, 2026-08-01. Was a 120ms opacity fade with the explicit note
   "NO SCALE"; is now `--dur-modal` of opacity plus `--enter-scale` plus
   `--enter-lift`, on `--ease-out`.

   The old rule was right about the thing it was guarding — a modal that *pops* is a
   modal that reads as an alert, and most of this product's modals are ordinary. But a
   pure cross-fade gives the eye no origin: the dialog is simply there, over content
   that is also still there, and for the first frames the two are equally weighted.
   0.98 and 4px are enough to say *this arrived on top* without anything visibly
   travelling.

   **Origin stays centre.** A popover scales from its trigger; a modal has no trigger
   in the spatial sense and appears in the middle of the viewport, so centre is
   correct here and is not an oversight.

   **Closing is instant.** The dialog unmounts. An exit transition would need the
   component to stay mounted through it, and the operator who has just pressed Cancel
   or Esc wants the thing gone, not gracefully leaving. Entrances can afford to be
   gracious; exits are the system getting out of the way. */

export function Modal({ open = true, title, width = 520, danger = false, onClose, lockEsc = false, footer, children, style }) {
  React.useEffect(() => {
    if (!open || lockEsc) return;
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, lockEsc, onClose]);

  if (!open) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 100, display: 'grid', placeItems: 'center', padding: 'var(--space-24)' }}>
      <div onClick={lockEsc ? undefined : onClose} className="jmt-scrim" style={{ position: 'absolute', inset: 0, background: 'var(--scrim)' }} />
      <div role="dialog" aria-modal="true" aria-label={title} className="jmt-modal" style={{
        position: 'relative', width, maxWidth: '100%', maxHeight: '100%', overflow: 'auto',
        background: 'var(--bg-overlay)', borderRadius: 'var(--radius-panel)',
        border: '1px solid ' + (danger ? 'var(--loss)' : 'var(--border-default)'),
        boxShadow: 'var(--shadow-overlay), var(--edge-highlight-strong)', ...style,
      }}>
        {title ? (
          <header style={{ padding: 'var(--pad-panel) var(--pad-panel) 0' }}>
            <h2 style={{
              margin: 0, fontSize: 'var(--title-size)', lineHeight: 'var(--title-line)',
              fontWeight: 'var(--title-weight)', letterSpacing: 'var(--title-tracking)',
              color: 'var(--text-primary)',
            }}>{title}</h2>
          </header>
        ) : null}
        <div style={{ padding: 'var(--pad-panel)' }}>{children}</div>
        {footer ? (
          <footer style={{
            display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-16)',
            padding: '0 var(--pad-panel) var(--pad-panel)',
          }}>{footer}</footer>
        ) : null}
        <style>{`
          @keyframes jmt-scrim-in{from{opacity:0}to{opacity:1}}
          @keyframes jmt-modal-in{
            from{opacity:0;transform:translateY(var(--enter-lift)) scale(var(--enter-scale))}
            to{opacity:1;transform:none}
          }
          .jmt-scrim{animation:jmt-scrim-in var(--dur-modal) var(--ease-out)}
          .jmt-modal{animation:jmt-modal-in var(--dur-modal) var(--ease-out)}
        `}</style>
      </div>
    </div>
  );
}
